import React, {useState, useEffect, useMemo} from 'react';
import {Modal, Row, Col} from 'react-bootstrap';
import {Button, Badge} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import axios from 'axios';
import SearchBox from '@/components/search/SearchBox';
import Spinner from '@/components/ui/Spinner';
import i18n from 'i18n';

const PROFILES_URL = '/internal_api/v1/admin/restriction-profiles';
const FIELDS_URL = '/internal_api/v1/admin/field-restrictions';

interface ProfileDetail {
    id: number;
    name: string;
    codename: string;
    description: string | null;
    platform_managed: boolean;
    fields: FieldItem[];
    field_count: number;
    role_ids: number[];
    role_count: number;
}

interface FieldItem {
    field_id: number;
    field_key: string;
    es_field_name: string | null;
    api_variants: string[];
    category: string;
    label: string;
}

interface AllFieldsResponse {
    fields: FieldItem[];
    categories: string[];
}

const getCategoryLabel = (category: string): string => {
    const labels: Record<string, string> = {
        pii: i18n.t('field_restrictions_category_pii.text', {defaultValue: 'Personal Information'}),
    };
    return labels[category] || (category.charAt(0).toUpperCase() + category.slice(1));
};

const CATEGORY_ICONS: Record<string, string> = {
    pii: 'mdi-lock',
};

const CATEGORY_ORDER: Record<string, number> = {
    pii: 0,
};

const CATEGORY_COLORS: Record<string, string> = {
    pii: '#5b6abf',
};

const RestrictionProfileDetailModal: React.FC<{
    profileId: number | null;
    onClose: () => void;
    onSaved: () => void;
}> = ({profileId, onClose, onSaved}) => {
    const [profile, setProfile] = useState<ProfileDetail | null>(null);
    const [allFields, setAllFields] = useState<FieldItem[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [selectedFieldIds, setSelectedFieldIds] = useState<Set<number>>(new Set());
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    const readOnly = profile?.platform_managed ?? false;

    useEffect(() => {
        if (!profileId) return;
        setLoading(true);
        setSelectedCategory(null);
        setSearch('');
        Promise.all([
            axios.get(`${PROFILES_URL}/${profileId}`),
            axios.get(FIELDS_URL),
        ]).then(([profileRes, fieldsRes]) => {
            const p: ProfileDetail = profileRes.data;
            const f: AllFieldsResponse = fieldsRes.data;
            setProfile(p);
            setAllFields(f.fields || []);
            const sorted = (f.categories || []).sort(
                (a: string, b: string) => (CATEGORY_ORDER[a] ?? 99) - (CATEGORY_ORDER[b] ?? 99)
            );
            setCategories(sorted);
            setSelectedFieldIds(new Set(p.fields.map(pf => pf.field_id)));
        }).catch(() => {
            setProfile(null);
        }).finally(() => setLoading(false));
    }, [profileId]);

    const toggleField = (fieldId: number) => {
        if (readOnly) return;
        setSelectedFieldIds(prev => {
            const next = new Set(prev);
            if (next.has(fieldId)) next.delete(fieldId);
            else next.add(fieldId);
            return next;
        });
    };

    const handleSelectAll = () => {
        if (readOnly || !selectedCategory) return;
        const ids = new Set(selectedFieldIds);
        allFields.filter(f => f.category === selectedCategory).forEach(f => ids.add(f.field_id));
        setSelectedFieldIds(ids);
    };

    const handleClearAll = () => {
        if (readOnly || !selectedCategory) return;
        const ids = new Set(selectedFieldIds);
        allFields.filter(f => f.category === selectedCategory).forEach(f => ids.delete(f.field_id));
        setSelectedFieldIds(ids);
    };

    const handleSave = async () => {
        if (!profile || readOnly) return;
        setSaving(true);
        setSaveError(null);
        try {
            await axios.put(`${PROFILES_URL}/${profile.id}`, {
                field_ids: Array.from(selectedFieldIds),
            });
            onSaved();
            onClose();
        } catch (err: any) {
            setSaveError(err?.response?.data?.error || i18n.t('restriction_profiles_save_failed.text', {defaultValue: 'Failed to save changes. Please try again.'}));
        } finally {
            setSaving(false);
        }
    };

    const categoryFields = useMemo(() => {
        if (!selectedCategory) return [];
        let filtered = allFields.filter(f => f.category === selectedCategory);
        if (search.trim()) {
            const q = search.toLowerCase();
            filtered = filtered.filter(f =>
                f.label.toLowerCase().includes(q) ||
                f.field_key.toLowerCase().includes(q) ||
                (f.es_field_name || '').toLowerCase().includes(q) ||
                (f.api_variants || []).some(v => v.toLowerCase().includes(q))
            );
        }
        return filtered;
    }, [allFields, selectedCategory, search]);

    const categoryStats = useMemo(() => {
        const stats: Record<string, {total: number; selected: number}> = {};
        for (const f of allFields) {
            const cat = f.category || 'other';
            if (!stats[cat]) stats[cat] = {total: 0, selected: 0};
            stats[cat].total++;
            if (selectedFieldIds.has(f.field_id)) stats[cat].selected++;
        }
        return stats;
    }, [allFields, selectedFieldIds]);

    const totalSelected = useMemo(() => selectedFieldIds.size, [selectedFieldIds]);

    const selectedCategorySelected = useMemo(() => {
        if (!selectedCategory) return 0;
        return allFields
            .filter(f => f.category === selectedCategory)
            .filter(f => selectedFieldIds.has(f.field_id))
            .length;
    }, [allFields, selectedCategory, selectedFieldIds]);

    const totalInCategory = useMemo(() => {
        if (!selectedCategory) return 0;
        return allFields.filter(f => f.category === selectedCategory).length;
    }, [allFields, selectedCategory]);

    if (!profileId) return null;

    const renderLeftPanel = () => (
        <div className="h-100 d-flex flex-column">
            <div className="mb-3">
                <h6 className="font-weight-bold mb-1">{i18n.t('field_restrictions_title.text', {defaultValue: 'Field restrictions'})}</h6>
                <p className="text-muted small mb-0">
                    {profile?.description || i18n.t('restriction_profiles_select_fields.text', {defaultValue: 'Select fields to include in this profile.'})}
                </p>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted small">
                    {i18n.t('field_restrictions_n_selected.text', {defaultValue: '{{count}} selected', count: totalSelected})}
                </span>
            </div>

            <div className="flex-grow-1" style={{overflowY: 'auto'}}>
                {categories.map(category => {
                    const stats = categoryStats[category] || {total: 0, selected: 0};
                    const iconName = CATEGORY_ICONS[category] || 'mdi-folder';
                    const color = CATEGORY_COLORS[category] || '#6c757d';

                    return (
                        <div
                            key={category}
                            className={`d-flex align-items-center justify-content-between p-3 mb-2 rounded border ${selectedCategory === category ? 'border-primary bg-light' : ''}`}
                            style={{cursor: 'pointer'}}
                            onClick={() => {setSelectedCategory(category); setSearch('');}}
                        >
                            <div className="d-flex align-items-center" style={{gap: '1rem'}}>
                                <div
                                    className="d-flex align-items-center justify-content-center rounded"
                                    style={{width: 36, height: 36, backgroundColor: `${color}15`}}
                                >
                                    <Icon name={iconName} style={{color}} />
                                </div>
                                <div>
                                    <div className="font-weight-bold">
                                        {i18n.t('field_restrictions_category_restrictions.text', {defaultValue: '{{category}} restrictions', category: getCategoryLabel(category)})}
                                    </div>
                                    <small className="text-muted">
                                        {i18n.t('field_restrictions_n_restrictions.text', {defaultValue: '{{count}} restrictions', count: stats.total})}
                                    </small>
                                </div>
                            </div>
                            <div className="d-flex align-items-center" style={{gap: '1rem'}}>
                                {stats.selected > 0 && (
                                    <Badge variant="primary" pill>{stats.selected}</Badge>
                                )}
                                <Icon name="mdi-chevron-right" className="text-muted" />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderRightPanel = () => {
        if (!selectedCategory) {
            return (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                    <div className="text-center">
                        <Icon name="mdi-arrow-left" style={{fontSize: '2rem'}} />
                        <p className="mt-2">{i18n.t('field_restrictions_select_category.text', {defaultValue: 'Select a category from the left panel'})}</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="h-100 d-flex flex-column">
                <div className="d-flex align-items-center mb-3">
                    <a
                        href="#"
                        className="text-dark mr-2"
                        onClick={(e) => {e.preventDefault(); setSelectedCategory(null); setSearch('');}}
                    >
                        <Icon name="mdi-arrow-left" />
                    </a>
                    <h6 className="font-weight-bold mb-0 mr-2">
                        {i18n.t('field_restrictions_category_restrictions.text', {defaultValue: '{{category}} restrictions', category: getCategoryLabel(selectedCategory)})}
                    </h6>
                    <Badge variant="primary" pill>
                        {i18n.t('field_restrictions_n_selected.text', {defaultValue: '{{count}} selected', count: selectedCategorySelected})}
                    </Badge>
                </div>

                <div className="mb-2">
                    <SearchBox
                        value={search}
                        onChange={setSearch}
                        placeholder={i18n.t('field_restrictions_search_placeholder.text', {defaultValue: 'Search fields...'})}
                        className="mb-0"
                    />
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <small className="text-muted">
                        {i18n.t('field_restrictions_showing_count.text', {defaultValue: 'Showing {{shown}} of {{total}}', shown: categoryFields.length, total: totalInCategory})}
                    </small>
                    {!readOnly && (
                        <div className="d-flex align-items-center" style={{gap: '0.5rem'}}>
                            <a
                                href="#"
                                className="text-primary small"
                                onClick={(e) => {e.preventDefault(); handleSelectAll();}}
                            >
                                {i18n.t('field_restrictions_select_all.text', {defaultValue: 'Select all'})}
                            </a>
                            <span className="text-muted small">&middot;</span>
                            <a
                                href="#"
                                className="text-primary small"
                                onClick={(e) => {e.preventDefault(); handleClearAll();}}
                            >
                                {i18n.t('field_restrictions_clear_all.text', {defaultValue: 'Clear all'})}
                            </a>
                        </div>
                    )}
                </div>

                <div className="flex-grow-1" style={{overflowY: 'auto'}}>
                    {categoryFields.map(field => {
                        const isSelected = selectedFieldIds.has(field.field_id);
                        const displayName = field.field_key;

                        return (
                            <label
                                key={field.field_id}
                                className="d-flex align-items-center py-2 border-bottom"
                                style={{cursor: readOnly ? 'default' : 'pointer'}}
                                htmlFor={`profile-field-${field.field_id}`}
                            >
                                <input
                                    type="checkbox"
                                    id={`profile-field-${field.field_id}`}
                                    checked={isSelected}
                                    onChange={() => toggleField(field.field_id)}
                                    disabled={readOnly}
                                    style={{width: 14, height: 14, marginRight: 12, flexShrink: 0}}
                                />
                                <div className="flex-grow-1">
                                    <span>{field.label}</span>
                                </div>
                                <Badge variant="light" className="text-muted">
                                    {displayName}
                                </Badge>
                            </label>
                        );
                    })}
                    {categoryFields.length === 0 && (
                        <p className="text-muted text-center py-4 mb-0">
                            {i18n.t('field_restrictions_no_results.text', {defaultValue: 'No fields match your search'})}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal show={!!profileId} onHide={onClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {profile?.name || i18n.t('restriction_profiles_profile.text', {defaultValue: 'Profile'})}
                    {profile?.platform_managed && (
                        <Badge variant="secondary" className="ml-2">{i18n.t('roles_system_badge.text', {defaultValue: 'System'})}</Badge>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{height: '50vh', padding: 0, display: 'flex', flexDirection: 'column'}}>
                {loading ? (
                    <div className="d-flex align-items-center justify-content-center h-100">
                        <Spinner text={i18n.t('sp_loading.text')} centered />
                    </div>
                ) : !profile ? (
                    <div className="d-flex align-items-center justify-content-center h-100">
                        <div className="alert alert-warning mb-0">
                            {i18n.t('restriction_profiles_load_failed.text', {defaultValue: 'Failed to load profile'})}
                        </div>
                    </div>
                ) : (
                    <Row style={{margin: 0, flex: 1, minHeight: 0, overflow: 'hidden'}}>
                        <Col md={5} className="h-100 border-right p-4" style={{overflowY: 'auto'}}>
                            {renderLeftPanel()}
                        </Col>
                        <Col md={7} className="h-100 p-4" style={{overflowY: 'auto'}}>
                            {renderRightPanel()}
                        </Col>
                    </Row>
                )}
            </Modal.Body>

            <Modal.Footer>
                {saveError && (
                    <div className="text-danger small mr-auto">{saveError}</div>
                )}
                <Button variant="secondary" onClick={onClose}>
                    {readOnly ? i18n.t('jt_close.text', {defaultValue: 'Close'}) : i18n.t('jt_cancel.text', {defaultValue: 'Cancel'})}
                </Button>
                {!readOnly && (
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        loading={saving}
                    >
                        {i18n.t('field_restrictions_apply.text', {defaultValue: 'Apply'})}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default RestrictionProfileDetailModal;
